import type { ProblemDetails } from './types';

/** Client-side error codes, in addition to the API's own `code` values. */
export const ClientErrorCode = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
} as const;

/** Shown for the API's RATE_LIMITED refusal (HTTP 429) instead of its raw text. */
export const RATE_LIMITED_MESSAGE =
  'Too many tries. Wait a minute and try again.';

/**
 * Every failed API call rejects with ApiError. UI code branches on `code`
 * (stable, from the API) and shows `requestId` so staff can quote it.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly problem?: ProblemDetails;
  readonly fieldErrors: { field: string; message: string }[];

  constructor(init: {
    status: number;
    code: string;
    message: string;
    requestId?: string;
    problem?: ProblemDetails;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.problem = init.problem;
    this.fieldErrors = init.problem?.errors ?? [];
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Field-level validation message for a form field, if the API sent one. */
  fieldError(field: string): string | undefined {
    return this.fieldErrors.find((e) => e.field === field)?.message;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isProblemDetails(body: unknown): body is ProblemDetails {
  return (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as ProblemDetails).status === 'number' &&
    typeof (body as ProblemDetails).title === 'string'
  );
}
